import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useEditorAuth } from "@/context/EditorAuthContext";
import { LogOut, Plus, Home as HomeIcon, Pencil, Trash2, Eye, FileText, Tag, Calendar, BarChart3, Bot } from "lucide-react";
import { toast } from "sonner";
import KeywordsPanel from "@/pages/editor/KeywordsPanel";
import CalendarPanel from "@/pages/editor/CalendarPanel";
import AnalyticsPanel from "@/pages/editor/AnalyticsPanel";
import AutoWriterPanel from "@/pages/editor/AutoWriterPanel";

export default function EditorDashboard() {
  const { user, loading, logout, authAxios } = useEditorAuth();
  const [articles, setArticles] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [tab, setTab] = useState("articles");
  const navigate = useNavigate();

  const load = () => {
    setDataLoading(true);
    authAxios().get("/editor/articles").then((r) => setArticles(r.data)).catch(() => {}).finally(() => setDataLoading(false));
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/editor/login" replace />;

  const onLogout = () => { logout(); navigate("/editor/login", { replace: true }); };

  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await authAxios().delete(`/editor/articles/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const stats = {
    total: articles.length,
    published: articles.filter(a => a.status === "published").length,
    drafts: articles.filter(a => a.status === "draft").length,
    scheduled: articles.filter(a => a.status === "scheduled").length,
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="editor-dashboard-page">
      <header className="bg-[#0A1128] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="https://customer-assets.emergentagent.com/job_notes-to-site/artifacts/cver0rlc_HappyHomeAgency%20Logo2026.png" alt="Happy Home" className="h-10 w-auto bg-white p-1"/>
            <div>
              <div className="font-serif text-xl">Happy Home · Blog Editor</div>
              <div className="text-xs text-[#A1A9BC]">Signed in as {user.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/blog" target="_blank" className="text-sm text-[#A1A9BC] hover:text-white flex items-center gap-2"><HomeIcon className="w-4 h-4"/>View Blog</Link>
            <button onClick={onLogout} data-testid="editor-logout" className="text-sm flex items-center gap-2 border border-white/20 px-4 py-2 hover:bg-white hover:text-[#0A1128] transition">
              <LogOut className="w-4 h-4"/> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="overline mb-2">Editor Workspace</div>
            <h1 className="font-serif text-4xl text-[#0A1128]">Content library.</h1>
            <div className="text-sm text-gray-500 mt-2">{stats.total} articles · {stats.published} published · {stats.drafts} drafts · {stats.scheduled} scheduled</div>
          </div>
          <Link to="/editor/articles/new" data-testid="editor-new-article" className="btn-primary">
            <Plus className="w-4 h-4"/> New Article
          </Link>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8 flex gap-8 flex-wrap">
          {[
            { k: "articles", label: "Articles", icon: FileText },
            { k: "keywords", label: "Keywords", icon: Tag },
            { k: "calendar", label: "Calendar", icon: Calendar },
            { k: "analytics", label: "Analytics", icon: BarChart3 },
            { k: "autowriter", label: "Auto-Writer", icon: Bot },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`pb-3 text-sm border-b-2 -mb-px transition flex items-center gap-2 ${tab === t.k ? "border-[#688A4E] text-[#0A1128] font-semibold" : "border-transparent text-gray-500 hover:text-[#0A1128]"}`}
            >
              <t.icon className="w-4 h-4"/> {t.label}
            </button>
          ))}
        </div>

        {tab === "keywords" ? (
          <KeywordsPanel />
        ) : tab === "calendar" ? (
          <CalendarPanel />
        ) : tab === "analytics" ? (
          <AnalyticsPanel />
        ) : tab === "autowriter" ? (
          <AutoWriterPanel />
        ) : dataLoading ? (
          <div className="text-gray-500">Loading…</div>
        ) : articles.length === 0 ? (
          <div className="bg-white border border-gray-200 p-16 text-center" data-testid="editor-empty-state">
            <div className="font-serif text-2xl text-[#0A1128] mb-3">No articles yet.</div>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">Publish your first article to start driving traffic to Happy Home Agency.</p>
            <Link to="/editor/articles/new" className="btn-primary">
              <Plus className="w-4 h-4"/> Write your first article
            </Link>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="editor-articles-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F9F7]">
                <tr className="text-left text-xs tracking-widest uppercase text-gray-500">
                  <th className="px-6 py-4">Title</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Tags</th>
                  <th className="px-6 py-4">Views</th>
                  <th className="px-6 py-4">Updated</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((a) => (
                  <tr key={a.id} className="border-t border-gray-100 hover:bg-[#F9F9F7]" data-testid={`article-row-${a.slug}`}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-[#0A1128]">{a.title || "(Untitled)"}</div>
                      <div className="text-xs text-gray-500">/{a.slug}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs px-2 py-1 border ${
                        a.status === "published" ? "border-[#688A4E] text-[#688A4E]" :
                        a.status === "scheduled" ? "border-amber-500 text-amber-600" :
                        "border-gray-300 text-gray-500"
                      }`}>{a.status}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {(a.tags || []).slice(0, 3).map((t) => <span key={t} className="text-[10px] border border-gray-300 px-1.5 py-0.5">{t}</span>)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{a.view_count || 0}</td>
                    <td className="px-6 py-4 text-xs text-gray-500">{new Date(a.updated_at).toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        {a.status === "published" && (
                          <Link to={`/blog/${a.slug}`} target="_blank" data-testid={`article-view-${a.slug}`} className="text-gray-500 hover:text-[#688A4E] p-1.5" title="View"><Eye className="w-4 h-4"/></Link>
                        )}
                        <Link to={`/editor/articles/${a.id}`} data-testid={`article-edit-${a.slug}`} className="text-gray-500 hover:text-[#0A1128] p-1.5" title="Edit"><Pencil className="w-4 h-4"/></Link>
                        <button onClick={() => del(a.id, a.title)} data-testid={`article-delete-${a.slug}`} className="text-gray-500 hover:text-red-600 p-1.5" title="Delete"><Trash2 className="w-4 h-4"/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
